import React, { FC, useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";

const NET_RATE_MIN_MINUTES = 5 * 60;
const NET_RATE_MAX_MINUTES = 21 * 60;

export interface NetRateSubmitPayload {
  confirmationNumber?: string;
  startTime?: string;
  teeDate?: string;
}

interface DeleteModalProps {
  message?: string;
  buttonText?: string;
  showModal: boolean;
  onCloseClick: (arg: any) => void;
  OnSubmit: (payload?: NetRateSubmitPayload) => void;
  action?: "accept" | "reject" | "updateConfirmation";
  initialConfirmationNumber?: string;
  initialStartTime?: string;
  initialTeeDate?: string;
}

const parseTime12hToMinutes = (timeStr: string): number | null => {
  const trimmed = timeStr.trim();
  const match = trimmed.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return null;
  let hour = Number(match[1]);
  const minute = Number(match[2]);
  const period = match[3].toUpperCase();
  if (period === "PM" && hour !== 12) hour += 12;
  if (period === "AM" && hour === 12) hour = 0;
  return hour * 60 + minute;
};

const minutesTo12hParts = (totalMinutes: number) => {
  const hour24 = Math.floor(totalMinutes / 60);
  const minute = totalMinutes % 60;
  const period = hour24 >= 12 ? "PM" : "AM";
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return { hour12, minute, period };
};

const formatStartTime = (hour12: number, minute: number, period: string): string =>
  `${hour12.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")} ${period}`;

const normalizeTimeDisplay = (time: string): string => {
  const parsed = parseTime12hToMinutes(time);
  if (parsed == null) return time.trim();
  const { hour12, minute, period } = minutesTo12hParts(parsed);
  return formatStartTime(hour12, minute, period);
};

/** e.g. "07:30 AM - 08:00 AM" → "07:30 AM to 08:00 AM" */
const formatRequestedTeeTime = (value?: string): string => {
  if (!value) return "-";
  const parts = value.split(/\s*-\s*/).map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 2) {
    return `${normalizeTimeDisplay(parts[0])} to ${normalizeTimeDisplay(parts[1])}`;
  }
  return normalizeTimeDisplay(parts[0] || value);
};

const StatusModal: FC<DeleteModalProps> = ({
  message = "Are you sure, you want to delete?",
  buttonText = "Yes, please delete.",
  showModal = false,
  onCloseClick,
  OnSubmit,
  action,
  initialConfirmationNumber,
  initialStartTime,
  initialTeeDate,
}) => {
  const [confirmationNumber, setConfirmationNumber] = useState("");
  const [teeHour, setTeeHour] = useState("");
  const [teeMinute, setTeeMinute] = useState("");
  const [teePeriod, setTeePeriod] = useState<"AM" | "PM">("AM");

  useEffect(() => {
    if (!showModal) {
      setConfirmationNumber("");
      setTeeHour("");
      setTeeMinute("");
      setTeePeriod("AM");
      return;
    }

    if ((action === "accept" || action === "updateConfirmation") && initialConfirmationNumber) {
      setConfirmationNumber(initialConfirmationNumber);
    }

    if (action === "accept") {
      setTeeHour("");
      setTeeMinute("");
      setTeePeriod("AM");
    }

    if (action === "updateConfirmation" && initialStartTime) {
      const timePart = initialStartTime.split(/\s*-\s*/)[0].trim();
      const parsed = parseTime12hToMinutes(normalizeTimeDisplay(timePart));
      if (parsed != null) {
        const { hour12, minute, period } = minutesTo12hParts(parsed);
        setTeeHour(String(hour12));
        setTeeMinute(String(minute));
        setTeePeriod(period as "AM" | "PM");
      } else {
        setTeeHour("");
        setTeeMinute("");
        setTeePeriod("AM");
      }
    }
  }, [showModal, action, initialConfirmationNumber, initialStartTime]);

  const isTeeTimeEntered = () =>
    (teeHour || "").trim() !== "" || (teeMinute || "").trim() !== "";

  const isTeeTimeComplete = () =>
    (teeHour || "").trim() !== "" && (teeMinute || "").trim() !== "";

  const buildTeeTimeString = (): string => {
    const hour12 = Number(teeHour);
    const minute = Number(teeMinute);
    return formatStartTime(hour12, minute, teePeriod);
  };

  const isTeeTimeValid = (): boolean => {
    if (!isTeeTimeEntered()) return true;
    if (!isTeeTimeComplete()) return false;
    const hour12 = Number(teeHour);
    const minute = Number(teeMinute);
    if (!Number.isInteger(hour12) || hour12 < 1 || hour12 > 12) return false;
    if (!Number.isInteger(minute) || minute < 0 || minute > 59) return false;
    const totalMinutes = parseTime12hToMinutes(buildTeeTimeString());
    if (totalMinutes == null) return false;
    return totalMinutes >= NET_RATE_MIN_MINUTES && totalMinutes <= NET_RATE_MAX_MINUTES;
  };

  const handleSubmit = () => {
    if (action === "accept" || action === "updateConfirmation") {
      const trimmed = (confirmationNumber || "").trim();
      if (!trimmed) return;
      if (action === "accept") {
        if (!isTeeTimeValid()) return;
        const payload: NetRateSubmitPayload = { confirmationNumber: trimmed };
        if (isTeeTimeEntered() && isTeeTimeComplete()) {
          const startTime = buildTeeTimeString();
          payload.startTime = startTime;
          if (initialTeeDate) payload.teeDate = initialTeeDate;
        }
        OnSubmit(payload);
      } else if (action === "updateConfirmation") {
        if (!isTeeTimeValid() || !isTeeTimeComplete() || !initialTeeDate) return;
        const startTime = buildTeeTimeString();
        OnSubmit({
          confirmationNumber: trimmed,
          startTime,
          teeDate: initialTeeDate,
        });
      }
      setConfirmationNumber("");
      setTeeHour("");
      setTeeMinute("");
    } else {
      OnSubmit();
    }
  };

  const requiresConfirmation = action === "accept" || action === "updateConfirmation";
  const canSubmit =
    requiresConfirmation &&
    (confirmationNumber || "").trim().length > 0 &&
    (action === "updateConfirmation"
      ? isTeeTimeValid() && isTeeTimeComplete()
      : action !== "accept" || isTeeTimeValid());

  const showTeeTimeError =
    (action === "accept" && isTeeTimeEntered() && !isTeeTimeValid()) ||
    (action === "updateConfirmation" && !isTeeTimeValid());

  return (
    <Modal
      className="cancelBookingModal modalLayoutWrapper"
      show={showModal}
      backdrop="static"
      keyboard={false}
      centered
    >
      <Modal.Body>
        <a href={void 0} className="closeButton" onClick={onCloseClick}>
          <em className="fa-solid fa-close"></em>
        </a>
        <div className="formWrap">
          <div className="text-center">
            <img
              src="../assets/images/spedometer-low.png"
              alt="deactivate"
              className="mb-4 d-inline-block"
            />
            <h3 className="mb-4">{message}</h3>
          </div>

          {action === "accept" && (
            <div className="mb-4 text-start">
              <label className="form-label fw600 color-700">Requested Teetime</label>
              <p className="color-800 mb-0">{formatRequestedTeeTime(initialStartTime)}</p>
            </div>
          )}

          {(action === "accept" || action === "updateConfirmation") && (
            <div className="mb-4 text-start">
              <label className="form-label fw600 color-700 mb-1">Teetime</label>
              <p className="fontsize-12 color-600 mb-2">
                {action === "updateConfirmation"
                  ? "Update the confirmed tee time (05:00 AM to 09:00 PM)."
                  : "Optional — leave blank to keep the requested time (05:00 AM to 09:00 PM)."}
              </p>
              <div className="row g-2 align-items-end">
                <div className="col-4">
                  <label className="fontsize-12 color-600">Hour</label>
                  <input
                    type="number"
                    className="form-control"
                    min={1}
                    max={12}
                    value={teeHour}
                    placeholder="—"
                    onChange={(e) => setTeeHour(e.target.value)}
                  />
                </div>
                <div className="col-4">
                  <label className="fontsize-12 color-600">Minute</label>
                  <input
                    type="number"
                    className="form-control"
                    min={0}
                    max={59}
                    value={teeMinute}
                    placeholder="—"
                    onChange={(e) => setTeeMinute(e.target.value)}
                  />
                </div>
                <div className="col-4">
                  <label className="fontsize-12 color-600">AM / PM</label>
                  <select
                    className="form-select"
                    value={teePeriod}
                    onChange={(e) => setTeePeriod(e.target.value as "AM" | "PM")}
                  >
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
              </div>
              {showTeeTimeError && (
                <p className="text-danger fontsize-12 mt-2 mb-0">
                  {action === "updateConfirmation"
                    ? "Enter a complete valid time between 05:00 AM and 09:00 PM."
                    : "Enter a complete valid time between 05:00 AM and 09:00 PM, or leave blank to keep the requested time."}
                </p>
              )}
            </div>
          )}

          {requiresConfirmation && (
            <div className="mb-4 text-start">
              <label className="form-label fw600 color-700">
                Confirmation number <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                value={confirmationNumber}
                onChange={(e) => setConfirmationNumber(e.target.value)}
                placeholder="Enter confirmation number"
                aria-required="true"
              />
            </div>
          )}

          <div className="text-center">
            <a
              href={void 0}
              className={`text-decoration-underline fw600 ${buttonText.includes("accept") ? "color-success" : "color-danger"}`}
              onClick={canSubmit ? handleSubmit : undefined}
              style={!canSubmit ? { opacity: 0.6, cursor: "not-allowed" } : undefined}
            >
              {buttonText}
            </a>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default StatusModal;
